import React, { useCallback, useEffect } from 'react';
import { Snackbar, Typography } from '@mui/material';
import { Alert } from '@mui/material';
import classNames from 'classnames';
import { SnackbarOrigin } from '@mui/material/Snackbar';
import { useMessage } from 'modules/intl';
import { SlideTransition } from 'modules/common/components';
import {
  SnackbarProps,
  useSnackbarStyles
} from 'modules/common/hooks/useSnackbarStyles';
import { useNotesToastContext } from './NotesToastContextProvider';

export type ShowSuccessToast = (showSuccess: boolean, message: string) => void;
export type ShowErrorToast = (showSuccess: boolean) => void;

const anchorOrigin: SnackbarOrigin = { vertical: 'bottom', horizontal: 'left' };

export const NoteToast: React.FC<SnackbarProps> = ({
  pinned = true
}: SnackbarProps) => {
  const {
    toastContext: { message, showError, showSuccess, showMessage, values },
    setToastContext,
    dismiss
  } = useNotesToastContext();

  const [open, setOpen] = React.useState(false);
  const snackbarClasses = useSnackbarStyles({ pinned });
  const snackbarErrorClasses = useSnackbarStyles({ pinned, category: 'error' });
  const snackbarInfoClasses = useSnackbarStyles({ pinned, category: 'info' });

  const handleClose = useCallback(() => {
    setOpen(false);
    setToastContext({
      message: 'toast.note.failure'
    });
  }, [setToastContext, setOpen]);

  useEffect(() => {
    if (showSuccess || showError || showMessage) {
      setOpen(true);
    }
  }, [showSuccess, showError, showMessage]);

  useEffect(() => {
    if (dismiss) {
      handleClose();
    }
  }, [dismiss, handleClose]);

  const intlMessage = useMessage(message, values);

  return (
    <Snackbar
      anchorOrigin={anchorOrigin}
      open={open}
      autoHideDuration={3000}
      onClose={handleClose}
      className={snackbarClasses.snackBar}
      TransitionComponent={SlideTransition}
    >
      <div>
        {showMessage ? (
          <Alert
            severity="info"
            variant="filled"
            data-testid="note-toast-message-info"
            className={classNames(
              snackbarClasses.item,
              snackbarInfoClasses.severity
            )}
          >
            <Typography variant="body1">{intlMessage}</Typography>
          </Alert>
        ) : null}
        {showSuccess ? (
          <Alert
            severity="success"
            variant="filled"
            data-testid="note-toast-message-success"
            className={classNames(
              snackbarClasses.item,
              snackbarClasses.severity
            )}
          >
            <Typography variant="body1">{intlMessage}</Typography>
          </Alert>
        ) : null}
        {showError ? (
          <Alert
            severity="error"
            variant="filled"
            data-testid="note-toast-message-failure"
            className={classNames(
              snackbarClasses.item,
              snackbarErrorClasses.severity
            )}
          >
            <Typography variant="body1">{intlMessage}</Typography>
          </Alert>
        ) : null}
      </div>
    </Snackbar>
  );
};
